import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import { showError, showSuccess } from "../../../../../core/alert/sweetAlertConfig";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";

interface ItemServiceCardDTO {
  id: number;
  comment?: string;
  itemId: number;
  itemTypeId: number;
  scheduledDateTime: string | null;
  scheduledEndDateTime: string | null;
  status: string;
  assignedUserId?: number;
  accountableUserId?: number | null;
  serviceDetails?: string;
  checklistTemplate?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface UserDTO {
  id: number;
  name: string;
}

interface UpdateItemServiceCardModuleProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  itemServiceCard: ItemServiceCardDTO;
}

type FieldType = "text" | "number" | "checkbox" | "date" | "textarea" | "select";
interface FieldDef {
  name: string;
  type: FieldType;
  required: boolean;
  unit?: string;
  options?: string[];
  width?: string;
}

type FooterType = "text" | "date" | "line" | "sign";
interface FooterField { id: string; label: string; type: FooterType; }

interface TableDef {
  id: string;
  title?: string;
  rows: number;
  rowHeight?: string;
  fields: Array<{ id: string; data: FieldDef }>;
  values?: Record<string, any>;
}

interface SavedSchemaV2 {
  version: 2;
  title: string;
  tables: Array<{
    title?: string;
    rows: number;
    rowHeight?: string;
    fields: FieldDef[];
    values?: Record<string, any>;
  }>;
  metaBox: { show: boolean; fields: string[]; values?: Record<string, string> };
  detailsRow: { show: boolean; rightCols: number; values?: Record<string, any> };
  footer: {
    columns: number;
    fields: Array<{ label: string; type: FooterType }>;
    values?: Record<string, any>;
  };
}

const UpdateItemServiceCardModule: React.FC<UpdateItemServiceCardModuleProps> = ({
  show,
  onHide,
  onSuccess,
  itemServiceCard,
}) => {
  const navigate = useNavigate();

  // ---------- helpers ----------
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const safeFormatForInput = (value: string | null): string => {
    if (!value) return "";
    // Already in YYYY-MM-DDTHH:mm ?
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    // Build local YYYY-MM-DDTHH:mm (not UTC)
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  // ---------- primary state ----------
  const [comment, setComment] = useState(itemServiceCard.comment || "");
  const [status, setStatus] = useState(itemServiceCard.status || "PENDING");
  const [scheduledDateTime, setScheduledDateTime] = useState(
    safeFormatForInput(itemServiceCard.scheduledDateTime || "")
  );
  const [scheduledEndDateTime, setScheduledEndDateTime] = useState(
    safeFormatForInput(itemServiceCard.scheduledEndDateTime || "")
  );
  const [accountableUserId, setAccountableUserId] = useState<number | null>(
    itemServiceCard.accountableUserId ?? null
  );

  const [users, setUsers] = useState<UserDTO[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ---------- checklist state ----------
  const [serviceDetails, setServiceDetails] = useState<string | null>(
    itemServiceCard.serviceDetails || itemServiceCard.checklistTemplate || null
  );
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [itemTypeName, setItemTypeName] = useState<string>("Maintenance Checklist");
  const [cardId] = useState<number>(itemServiceCard.id);

  const [title, setTitle] = useState("");
  const [metaFields, setMetaFields] = useState<Array<{ id: string; label: string }>>([]);
  const [metaValues, setMetaValues] = useState<Record<string, string>>({});
  const [tables, setTables] = useState<TableDef[]>([]);
  const [footerFields, setFooterFields] = useState<FooterField[]>([]);
  const [footerCols, setFooterCols] = useState(1);
  const [showMetaBox, setShowMetaBox] = useState(false);
  const [showDetailsRow, setShowDetailsRow] = useState(false);

  // ---------- users fetch ----------
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await apiDefinitions.getUsers("1", {});
      if (response.status === 200) {
        setUsers(response.data.data.users || []);
      } else {
        showError(response.data?.statusDescription || "Failed to fetch users");
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || "Error fetching users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchItemServiceCard = async () => {
      setIsLoading(true);
      try {
        const response = await apiDefinitions.getItemServiceCardById(cardId);
        if (response?.data?.status === "S1000") {
          const raw: SavedSchemaV2 | string =
            response.data.data.serviceDetails || response.data.data.checklistTemplate;

          const applyParsed = (parsed: SavedSchemaV2) => {
            setServiceDetails(JSON.stringify(parsed));
            setTitle(parsed.title || "Checklist");
            setShowMetaBox(!!parsed.metaBox?.show);
            setMetaFields(
              (parsed.metaBox?.fields || []).map((f: string, i: number) => ({
                id: `meta-${i}`,
                label: f,
              }))
            );
            setMetaValues(parsed.metaBox?.values || {});
            setTables(
              (parsed.tables || []).map((t, idx) => ({
                id: `table-${idx}`,
                title: t.title,
                fields: (t.fields || []).map((f, fi) => ({
                  id: `field-${idx}-${fi}`,
                  data: f,
                })),
                rows: t.rows,
                values: t.values || {},
                rowHeight: t.rowHeight,
              }))
            );
            setFooterFields(
              (parsed.footer?.fields || []).map((f, i) => ({
                id: `footer-${i}`,
                label: f.label,
                type: f.type,
              }))
            );
            setFooterCols(parsed.footer?.columns || 1);
            setShowDetailsRow(!!parsed.detailsRow?.show);
          };

          if (typeof raw === "string" && raw.trim()) {
            try {
              applyParsed(JSON.parse(raw));
            } catch {
              showError("Invalid JSON in serviceDetails or checklistTemplate");
            }
          } else if (raw && typeof raw === "object") {
            applyParsed(raw as SavedSchemaV2);
          }
        } else {
          showError(response?.data?.statusDescription || "Failed to fetch item service card");
        }
      } catch (error: any) {
        if (error?.response?.status === 401) {
          showError("Unauthorized: Invalid or expired token. Please log in again.");
          localStorage.removeItem("token");
          navigate(all_routes.login);
        } else {
          showError(error?.response?.data?.statusDescription || "Error fetching item service card");
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (cardId) fetchItemServiceCard();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, navigate]);

  // Keep form responsive to incoming prop changes (before response completes)
  useEffect(() => {
    setComment(itemServiceCard.comment || "");
    setStatus(itemServiceCard.status || "PENDING");
    setScheduledDateTime(safeFormatForInput(itemServiceCard.scheduledDateTime || ""));
    setScheduledEndDateTime(safeFormatForInput(itemServiceCard.scheduledEndDateTime || ""));
    setAccountableUserId(itemServiceCard.accountableUserId ?? null);
    setServiceDetails(itemServiceCard.serviceDetails || itemServiceCard.checklistTemplate || null);
    setItemTypeName("Maintenance Checklist");
  }, [itemServiceCard]);

  // -------- checklist runtime state from parsed initial --------
  const uid = () => Math.random().toString(36).slice(2, 9);

  const initialState = useMemo(() => {
    let title = itemTypeName || "Checklist";
    let tables: TableDef[] = [];
    let metaBox = { show: false, fields: [] as string[], values: {} as Record<string, string> };
    let detailsRow = { show: false, rightCols: 0, values: {} as Record<string, any> };
    let footer = { columns: 0, fields: [] as FooterField[], values: {} as Record<string, any> };
    const tempTableValues: Record<string, any> = {};
    const tempDetailsValues: Record<string, any> = {};

    if (serviceDetails?.trim()) {
      try {
        const parsed = JSON.parse(serviceDetails) as SavedSchemaV2;
        if (parsed?.version === 2) {
          const s = parsed;
          title = s.title ?? title;
          tables = (Array.isArray(s.tables) && s.tables.length
            ? s.tables
            : [{ title: "", rows: 5, rowHeight: "40px", fields: [], values: {} }]
          ).map((t) => ({
            id: uid(),
            title: t.title,
            rows: Number.isFinite(t.rows) ? t.rows : 5,
            rowHeight: t.rowHeight || "40px",
            fields: (t.fields || []).map((f) => ({ id: uid(), data: { ...f, width: f.width || "auto" } })),
            values: {},
          }));

          metaBox = {
            show: !!s.metaBox?.show,
            fields: s.metaBox?.fields || [],
            values: s.metaBox?.values || {},
          };

          detailsRow = {
            show: !!s.detailsRow?.show,
            rightCols: s.detailsRow?.rightCols || 0,
            values: s.detailsRow?.values || {},
          };

          footer = {
            columns: Math.max(0, Math.min(4, s.footer?.columns ?? 0)),
            fields: (s.footer?.fields || []).map((f, i) => ({
              id: Object.keys(s.footer?.values || {})[i] || uid(),
              label: f.label,
              type: f.type,
            })),
            values: s.footer?.values || {},
          };

          // re-key values to new table ids
          s.tables.forEach((t, index) => {
            const newTable = tables[index];
            const oldValues = t.values || {};
            const oldKeys = Object.keys(oldValues);
            if (oldKeys.length > 0) {
              const oldId = oldKeys[0].split(":")[0];
              for (const key of oldKeys) {
                const newKey = key.replace(oldId, newTable.id);
                tempTableValues[newKey] = (oldValues as any)[key];
              }
              const dv = (s.detailsRow?.values || {}) as Record<string, any>;
              Object.keys(dv).forEach((k) => {
                if (k.startsWith(`details:${oldId}:`)) {
                  const newKey = k.replace(`details:${oldId}:`, `details:${newTable.id}:`);
                  tempDetailsValues[newKey] = dv[k];
                }
              });
            }
          });
        }
      } catch {
        // ignore
      }
    }

    if (tables.length === 0) {
      tables = [{ id: uid(), title: "", rows: 5, rowHeight: "40px", fields: [], values: {} }];
    }

    return { title, tables, metaBox, detailsRow, footer, tempTableValues, tempDetailsValues };
  }, [serviceDetails, itemTypeName]);

  const [tableValues, setTableValues] = useState<Record<string, any>>(initialState.tempTableValues);
  const [detailsValues, setDetailsValues] = useState<Record<string, any>>(initialState.tempDetailsValues);
  const [footerValues, setFooterValues] = useState<Record<string, any>>(initialState.footer.values);

  // ---------- submit ----------
  const handleSubmit = async () => {
    if (!scheduledDateTime) {
      showError("Please provide a scheduled date and time");
      return;
    }
    if (!accountableUserId) {
      showError("Please select an accountable user");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        id: itemServiceCard.id,
        comment,
        status,
        scheduledDateTime,
        scheduledEndDateTime: scheduledEndDateTime || null,
        accountableUserId,
        serviceDetails,
      };
      const response = await apiDefinitions.updateItemServiceCard(payload);
      if (response?.data?.status === "S1000") {
        showSuccess("Item service card updated successfully");
        onSuccess();
        onHide();
      } else {
        showError(response?.data?.statusDescription || "Failed to update item service card");
      }
    } catch (error: any) {
      showError(error?.response?.data?.statusDescription || "Error updating item service card");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- render helpers ----------
  const cellKey = (tableId: string, r: number, cName: string) => `${tableId}:${r}:${cName}`;

  const renderEntryCell = (tableId: string, f: FieldDef, r: number) => {
    const k = cellKey(tableId, r, f.name);
    const v = tableValues[k];
    const set = (val: any) => setTableValues((prev) => ({ ...prev, [k]: val }));

    switch (f.type) {
      case "text":
        return <Form.Control className="tb-input" value={v ?? ""} onChange={(e) => set(e.target.value)} />;
      case "number":
        return (
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              className="tb-input"
              type="number"
              value={v ?? ""}
              onChange={(e) => set(e.target.value === "" ? "" : Number(e.target.value))}
              style={{ maxWidth: "120px" }}
            />
            {f.unit && <span className="text-muted">{f.unit}</span>}
          </div>
        );
      case "date":
        return (
          <Form.Control
            className="tb-input"
            type="date"
            value={v ?? ""}
            onChange={(e) => set(e.target.value)}
          />
        );
      case "checkbox":
        return (
          <Form.Check className="ms-1">
            <Form.Check.Input id={`chk_${k}`} checked={!!v} onChange={(e) => set(e.target.checked)} />
            <Form.Check.Label htmlFor={`chk_${k}`}>Yes</Form.Check.Label>
          </Form.Check>
        );
      case "textarea":
        return (
          <Form.Control as="textarea" className="tb-textarea" value={v ?? ""} onChange={(e) => set(e.target.value)} />
        );
      case "select":
        return (
          <Form.Select className="tb-select" value={v ?? ""} onChange={(e) => set(e.target.value)}>
            <option value="" disabled>
              {f.required ? "Select… (required)" : "Select…"}
            </option>
            {(f.options ?? []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
        );
      default:
        return null;
    }
  };

  const renderDetailsCell = (tableId: string, c: { id: string; data: FieldDef }) => {
    const k = `details:${tableId}:${c.data.name}`;
    const v = detailsValues[k];
    const set = (val: any) => setDetailsValues((prev) => ({ ...prev, [k]: val }));
    return (
      <td key={`details-${c.id}`} style={{ width: c.data.width || "auto" }}>
        <Form.Control
          as="textarea"
          className="tb-textarea"
          value={v ?? ""}
          placeholder="Notes / Description"
          onChange={(e) => set(e.target.value)}
        />
      </td>
    );
  };

  const renderFooterField = (f: FooterField) => {
    const k = f.id;
    const v = footerValues[k];
    const set = (val: any) => setFooterValues((prev) => ({ ...prev, [k]: val }));
    return (
      <div className="tb-foot-item" key={f.id}>
        <div className="lbl">{f.label}</div>
        {f.type === "sign" && <div className="sign" />}
        {f.type === "line" && <div className="line" />}
        {f.type === "text" && <Form.Control className="tb-input" value={v ?? ""} onChange={(e) => set(e.target.value)} />}
        {f.type === "date" && (
          <Form.Control className="tb-input" type="date" value={v ?? ""} onChange={(e) => set(e.target.value)} />
        )}
      </div>
    );
  };

  const handleChecklistSave = () => {
    const schema: SavedSchemaV2 = {
      version: 2,
      title: title.trim(),
      tables: tables.map((t) => ({
        title: t.title?.trim(),
        rows: Math.max(5, Math.min(50, t.rows | 0)),
        rowHeight: t.rowHeight?.trim(),
        fields: t.fields.map((c) => {
          const base: FieldDef = { name: c.data.name.trim(), type: c.data.type, required: !!c.data.required };
          if (c.data.type === "number" && c.data.unit) base.unit = c.data.unit;
          if (c.data.type === "select" && c.data.options?.length) base.options = c.data.options;
          if (c.data.width?.trim()) base.width = c.data.width.trim();
          return base;
        }),
        values: Object.keys(tableValues)
          .filter((k) => k.startsWith(`${t.id}:`))
          .reduce((acc, k) => ({ ...acc, [k]: tableValues[k] }), {}),
      })),
      metaBox: { show: showMetaBox, fields: metaFields.map((m) => m.label), values: metaValues },
      detailsRow: { show: showDetailsRow, rightCols: 0, values: detailsValues },
      footer: {
        columns: footerCols,
        fields: footerFields.map((f) => ({ label: f.label, type: f.type })),
        values: footerValues,
      },
    };
    setServiceDetails(JSON.stringify(schema));
    setShowChecklistModal(false);
  };

  const printPdf = () => window.print();

  useEffect(() => {
    const modalId = "advanced_table_checklist_template_modal";
    const styleId = `adv-table-style-${modalId}`;
    if (!document.getElementById(styleId)) {
      const s = document.createElement("style");
      s.id = styleId;
      s.innerHTML = `
        .tb-preview-wrap { width:100%; }
        .tb-a4 { width:100%; max-width:210mm; background:#fff; box-shadow:0 0 0.5cm rgba(0,0,0,.2); padding:12mm; margin:0 auto; color:#222; }
        .tb-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:10px; }
        .tb-title { font-weight:700; font-size:18px; }
        .tb-meta { border:1px solid #bbb; border-collapse:collapse; }
        .tb-meta th, .tb-meta td { border:1px solid #bbb; padding:4px 8px; font-size:12px; }
        .tb-meta th { background:#f6f6f6; text-align:left; }
        .tb-table { width:100%; border-collapse:collapse; table-layout: fixed; margin-bottom:12mm; }
        .tb-table th, .tb-table td { border:1px solid #999; padding:6px 8px; font-size:12px; vertical-align:top; }
        .tb-table th { background:#f3f3f3; text-align:left; }
        .tb-input, .tb-select, .tb-textarea { width:100%; border:1px solid #cfd3d8; border-radius:6px; padding:6px 8px; font-size:12px; }
        .tb-textarea { min-height:40px; resize:vertical; }
        .tb-box { width:16px; height:16px; border:2px solid #666; border-radius:3px; display:inline-block; vertical-align:middle; }
        .tb-footgrid { display:grid; gap:8mm; margin-top:8mm; }
        .tb-foot-item .lbl { font-weight:600; margin-bottom:4px; font-size:12px; }
        .tb-foot-item .sign { height:24mm; border:1px dashed #cbd5e1; border-radius:6px; }
        .tb-foot-item .line { height:18px; border-bottom:1px solid #bbb; }
        @media print {
          @page { size: A4; margin: 12mm; }
          body * { visibility: hidden !important; }
          .tb-a4, .tb-a4 * { visibility: visible !important; }
          .tb-a4 { position:absolute; left:0; top:0; width:210mm; }
          .modal, .modal-backdrop { display:none !important; }
        }
      `;
      document.head.appendChild(s);
    }
    return () => { document.getElementById(styleId)?.remove(); };
  }, []);

  // ---------- UI ----------
  const accountableExistsInList = users.some((u) => u.id === accountableUserId);

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Item Service Card #{itemServiceCard.id}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* === Your requested block (now resilient BEFORE response) === */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Scheduled DateTime</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={scheduledDateTime}
                    onChange={(e) => setScheduledDateTime(e.target.value)}
                    onBlur={(e) => setScheduledDateTime(safeFormatForInput(e.target.value))}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Scheduled End DateTime</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={scheduledEndDateTime}
                    min={scheduledDateTime || undefined}
                    onChange={(e) => setScheduledEndDateTime(e.target.value)}
                    onBlur={(e) => setScheduledEndDateTime(safeFormatForInput(e.target.value))}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Accountable User</Form.Label>
                  <Form.Select
                    value={accountableUserId ?? ""}
                    // keep enabled so a temp option can show even while loading
                    onChange={(e) => setAccountableUserId(Number(e.target.value) || null)}
                    required
                  >
                    <option value="">{isLoading ? "Loading users…" : "Select a user"}</option>

                    {/* Show the currently selected id even if users not yet loaded */}
                    {!accountableExistsInList && accountableUserId && (
                      <option value={accountableUserId}>
                        Selected user (ID {accountableUserId})
                      </option>
                    )}

                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="PENDING">Pending</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {/* === /requested block === */}

            <Form.Group className="mb-3">
              <Form.Label>Comment</Form.Label>
              <Form.Control as="textarea" value={comment} onChange={(e) => setComment(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Service Details (Checklist Template)</Form.Label>
              <Button variant="outline-secondary" onClick={() => setShowChecklistModal(true)} className="w-100">
                Edit Checklist Template
              </Button>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={isSubmitting || isLoading}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSubmit} disabled={isSubmitting || isLoading}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Checklist modal (unchanged structurally) */}
      <Modal show={showChecklistModal} onHide={() => setShowChecklistModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Fill Checklist</Modal.Title>
          <Button
            variant="success"
            size="sm"
            onClick={printPdf}
            disabled={!tables.some((t) => t.fields.length)}
          >
            <i className="ti ti-printer me-1" /> Download / Print
          </Button>
        </Modal.Header>

        <Modal.Body>
          <div className="tb-preview-wrap">
            <div className="tb-a4">
              <div className="tb-head">
                <div>
                  <div className="tb-title">{title.trim() || "Checklist"}</div>
                  <div style={{ color: "#666", fontSize: 12, marginTop: 2 }}>
                    Generated: {new Date().toLocaleDateString()}
                  </div>
                </div>

                {showMetaBox && metaFields.length > 0 && (
                  <table className="tb-meta">
                    <thead>
                      <tr><th colSpan={2}>Details</th></tr>
                    </thead>
                    <tbody>
                      {metaFields.map((m) => (
                        <tr key={m.id}>
                          <td style={{ minWidth: 90 }}>{m.label}</td>
                          <td style={{ minWidth: 160 }}>
                            <Form.Control
                              className="tb-input"
                              value={metaValues[m.label] ?? ""}
                              onChange={(e) =>
                                setMetaValues((prev) => ({ ...prev, [m.label]: e.target.value }))
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {tables.map((table) => (
                <div key={table.id}>
                  {table.title?.trim() && <div className="fw-semibold mb-2">{table.title.trim()}</div>}
                  <table className="tb-table">
                    <thead>
                      <tr style={{ height: table.rowHeight || "40px" }}>
                        {table.fields.map((c) => (
                          <th key={c.id} style={{ width: c.data.width || "auto" }}>
                            {c.data.name || "—"}
                            {c.data.required && <span style={{ color: "#d32f2f", marginLeft: 6 }}>*</span>}
                            {c.data.type === "number" && c.data.unit && (
                              <span style={{ marginLeft: 6, fontWeight: 400, color: "#666" }}>
                                ({c.data.unit})
                              </span>
                            )}
                          </th>
                        ))}
                        {!table.fields.length && <th>No fields defined</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {showDetailsRow && table.fields.length > 0 && (
                        <tr style={{ height: table.rowHeight || "40px" }}>
                          {table.fields.map((c) => renderDetailsCell(table.id, c))}
                        </tr>
                      )}
                      {Array.from({ length: table.rows }).map((_, r) => (
                        <tr key={`r-${r}`} style={{ height: table.rowHeight || "40px" }}>
                          {table.fields.map((c) => (
                            <td key={c.id} style={{ width: c.data.width || "auto" }}>
                              {renderEntryCell(table.id, c.data, r)}
                            </td>
                          ))}
                          {!table.fields.length && <td />}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {footerCols > 0 && footerFields.length > 0 && (
                <div className="tb-footgrid" style={{ gridTemplateColumns: `repeat(${footerCols}, 1fr)` }}>
                  {footerFields.map((f) => renderFooterField(f))}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowChecklistModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={handleChecklistSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateItemServiceCardModule;
